import {
  CaretDownIcon,
  ListMagnifyingGlassIcon,
  TrashSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Select as AntdSelect } from 'antd';
import type { ComponentProps, ReactNode } from 'react';

import styles from './select.module.scss';

type AntdSelectProps = Omit<
  ComponentProps<typeof AntdSelect>,
  'size' | 'suffixIcon' | 'allowClear' | 'removeIcon' | 'tagRender'
>;

export type SelectSize = 'default' | 'compact';

export type SelectProps = AntdSelectProps & {
  selectSize?: SelectSize;
  allowClear?: boolean;
  /**
   * Opt-in empty state (Figma 216:264119): renders the designed panel — muted list-magnifier glyph
   * above this centred message — instead of antd's default "No data". Left unset, the popup keeps
   * antd's default; an explicit `notFoundContent` still wins.
   */
  emptyText?: ReactNode;
};

export function Select({
  selectSize = 'default',
  allowClear = false,
  emptyText,
  notFoundContent,
  className,
  classNames,
  ...rest
}: SelectProps) {
  return (
    <AntdSelect
      // Search the visible name, not `value`: antd's default, and most options carry an id there,
      // so typing a name matched nothing (ADO 120965). A caller's own `showSearch` object wins.
      optionFilterProp="label"
      {...rest}
      notFoundContent={
        notFoundContent === undefined && emptyText !== undefined ? (
          <div className={styles.empty}>
            <ListMagnifyingGlassIcon className={styles.emptyIcon} size={20} aria-hidden />
            <span>{emptyText}</span>
          </div>
        ) : (
          notFoundContent
        )
      }
      suffixIcon={<CaretDownIcon size={14} aria-hidden />}
      allowClear={
        allowClear ? { clearIcon: <TrashSimpleIcon size={14} aria-label="Clear" /> } : false
      }
      tagRender={({ label, closable, onClose }) => (
        <span className={styles.tag}>
          <span className={styles.tagLabel} title={typeof label === 'string' ? label : undefined}>
            {label}
          </span>
          {closable ? (
            <button
              type="button"
              className={styles.tagRemove}
              aria-label={`Remove ${String(label)}`}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={onClose}
            >
              <XIcon size={10} aria-hidden />
            </button>
          ) : null}
        </span>
      )}
      classNames={{
        ...classNames,
        popup: {
          root: [styles.popup, (classNames as { popup?: { root?: string } })?.popup?.root]
            .filter(Boolean)
            .join(' '),
        },
      }}
      className={[styles.select, selectSize === 'compact' ? styles.compact : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}
